import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai spouse-visa document checklist guide. */
const TITLE = "Spouse Visa Documents from Thailand — Checklist and Certification Order";
const DESC =
  "Which Thai documents a spouse or partner visa file needs, the order in which they must be certified and translated, and the errors that most often cause a refusal.";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Which Thai documents does a spouse or partner visa file usually need?",
    answer:
      "Typically the marriage registration and marriage certificate, house registration, national identity card, birth certificate where relationship or identity must be proved, name-change records if any, and a police clearance certificate where the destination requires one. The exact list is set by the destination authority, so treat this as a planning baseline rather than a final list.",
  },
  {
    question: "In what order should documents be certified and translated?",
    answer:
      "Obtain current originals, notarise any private documents such as affidavits or statements, certify the Thai originals with the Department of Consular Affairs where required, translate into the language the authority accepts, certify the translation if asked, and finally complete any embassy or apostille step.",
  },
  {
    question: "Do translations need to be certified as well?",
    answer:
      "Usually yes. Most authorities require the translation to be certified or produced by a practitioner they recognise. Australian applications generally expect a NAATI-certified translation, while other destinations may accept certification by the Thai Department of Consular Affairs.",
  },
  {
    question: "How recent must the documents be?",
    answer:
      "Many authorities require civil records and police certificates issued within three or six months of submission. Plan backwards from the submission date, because certifying a document too early can make an otherwise perfect file unusable.",
  },
  {
    question: "What relationship evidence is expected alongside the certified documents?",
    answer:
      "Certified civil documents prove the legal relationship; separately, most authorities want evidence of a genuine and continuing relationship, such as shared accommodation records, joint financial arrangements, travel history, and communication history. Organise this evidence chronologically rather than as a loose bundle.",
  },
  {
    question: "Can one partner arrange the Thai documents alone?",
    answer:
      "Often yes, through a power of attorney, but the wording must cover every step: requesting the record, collecting it, submitting it for certification, and lodging it with the embassy. A narrowly drafted authority stalls halfway and has to be redrafted, which costs more time than drafting it broadly at the start.",
  },
  {
    question: "What causes most refusals at the document stage?",
    answer:
      "Name spellings that differ between the passport and the Thai records, incomplete translations that omit seal text, certifications performed out of order, documents outside the validity window, and missing name-change or divorce records that explain a discrepancy in the file.",
  },
];

export const Route = createFileRoute("/en/blog/spouse-visa-document-checklist")({
  component: EnSpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "spouse visa documents thailand, partner visa thai marriage certificate translation, thai marriage documents certification, kor ror 2 translation, thai police clearance for visa",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; note: string }> = [
  {
    doc: "Marriage registration and marriage certificate",
    note: "Request a current certified issue from the district office; older household copies are often refused.",
  },
  {
    doc: "House registration",
    note: "Confirm the current address entry matches the address stated elsewhere in the application.",
  },
  {
    doc: "National identity card",
    note: "Check that the name and title match the passport, including any recent change.",
  },
  {
    doc: "Birth certificate",
    note: "Needed where the authority verifies identity or parentage, particularly when children are included.",
  },
  {
    doc: "Name-change or divorce records",
    note: "Any discrepancy in the file must be explained by a document, not by a covering letter.",
  },
  {
    doc: "Police clearance certificate",
    note: "Time this to the submission date, since destination authorities usually set a validity window.",
  },
];

const SEQUENCE: string[] = [
  "Confirm the destination checklist, accepted translation type, and validity window",
  "Request current certified originals from the Thai issuing offices",
  "Notarise private documents such as statements, affidavits, and sponsorship letters",
  "Certify the Thai originals where the destination requires it",
  "Translate the complete set, including seal and stamp text",
  "Certify the translations and complete the embassy or apostille step",
];

const MISTAKES: string[] = [
  "Different transliterations of the same name across documents in one file",
  "Translating before certification, so certification never appears in the translated set",
  "Submitting photocopies where an original or authority-certified copy is required",
  "Documents issued too early and outside the destination validity window",
  "Relationship evidence presented as an unsorted bundle with no chronology",
  "A power of attorney drafted too narrowly to cover the whole certification chain",
];

function EnSpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="en" />
      <p className="answer-summary speakable-summary">
        A spouse or partner visa file built on Thai documents rarely fails because a document is
        missing. It fails because the documents were certified in the wrong order, translated by a
        practitioner the authority does not recognise, or issued too early to sit inside the validity
        window. Fix the destination submission date first, then work backwards through the embassy
        step, the translation, and the certification of the originals.
      </p>

      <h2>1. The document set</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}:</strong> {d.note}
          </li>
        ))}
      </ul>

      <h2>2. The certification sequence</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Consistency is the real test</h2>
      <p>
        Case officers compare documents against each other. If the passport, identity card, marriage
        record, and translation disagree about a name, a date, or an address, the file raises a
        question the officer must resolve — and the fastest resolution available to them is a request
        for more evidence or a refusal. Before submission, read the whole set as one document and
        make every field agree, using an official record to explain any change rather than a note.
      </p>

      <h2>4. Relationship evidence alongside the certified set</h2>
      <p>
        Certified civil documents establish the legal relationship; separately, the authority assesses
        whether the relationship is genuine and continuing. Present that evidence chronologically:
        shared accommodation, joint financial arrangements, travel together, and continuous
        communication. A short index at the front of the bundle makes the officer's job easier, which
        works in the applicant's favour.
      </p>

      <h2>5. Mistakes that cause refusals</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          Bureau of Registration Administration, Department of Provincial Administration
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/marriage" className="text-primary underline">
            Marriage and family document service
          </Link>
        </li>
        <li>
          <Link to="/en/naati" className="text-primary underline">
            NAATI-certified translation service
          </Link>
        </li>
        <li>
          <Link to="/en/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille vs legalisation, country by country
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not a determination made on behalf of any authority. Please confirm current requirements
        with the destination authority before you proceed.
      </p>
    </article>
  );
}
